import React, { useEffect, useState, Suspense } from 'react';
import './App.css';
import { BrowserRouter, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { DotLoader } from 'react-spinners';
import { useMediaQuery, useTheme } from '@mui/material';
import MetaWrapper from './MetaWrapper';
import ErrorPage from './components/ErrorPage';
import { DEVOPS_AND_CLOUD_PROGRAM, DEVOPS_CERTIFICATION, EXECUTIVE_PG_DEVOPS_PROGRAM, EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT } from './constants/AzureDevops';
// Eager import: the /devops-training-online-tih-iit* routes are primary ad landing
// pages — lazy-loading this chunk added a second network round-trip before the hero
// (the LCP element) could render.
import DevopsV3Main from './DevopsV3/DevopsV3Main';
// Eager import (same reasoning as DevopsV3Main above): /devops-training-online is an
// ad landing page that must be prerendered by react-snap. As a lazy route it created a
// nested double-lazy (lazy route -> lazy sections) that raced react-snap's crawl and
// aborted a CSS chunk request, leaving the snapshot an empty shell.
import DevopsV2Main from './DevopsV2/DevopsV2Main';
// Eager import (same reasoning as DevopsV2Main/DevopsV3Main above): "/" and
// "/blog/:slug" are react-snap-prerendered, high-traffic pages. As lazy routes,
// hydration has to wait for the chunk to arrive before it can render anything,
// so React shows the Suspense fallback (a small centered spinner) in place of
// the already-present prerendered content, then replaces it once the chunk
// loads — a near-full-viewport layout shift on every single page load,
// identical in magnitude across every route since it's the same fallback
// markup everywhere. That was the dominant, sitewide CLS regression.
import LandingPage from './LandingPageComponents/LandingPage';
import BlogDetail from './BlogsDashboard/BlogDetail';
const RefundPolicy = React.lazy(() => import('./TermsAndCondition/RefundPolicy'));
const About = React.lazy(() => import('./About/About'));
const Privacy = React.lazy(() => import('./Privacy/Privacy'));
const AWSMain = React.lazy(() => import('./AWS/AWSMain'));
const BlogOne = React.lazy(() => import('./Blogs/BlogOne'));
const BlogTwo = React.lazy(() => import('./Blogs/BlogTwo'));
const BlogThree = React.lazy(() => import('./Blogs/BlogThree'));
const BlogFour = React.lazy(() => import('./Blogs/BlogFour'));
const BlogSix = React.lazy(() => import('./Blogs/BlogSix'));
const BlogSeven = React.lazy(() => import('./Blogs/BlogSeven'));
const BlogEight = React.lazy(() => import('./Blogs/BlogEight'));
const BlogNine = React.lazy(() => import('./Blogs/BlogNine'));
const AzureMain = React.lazy(() => import('./Azure/AzureMain'));
const GCPMain = React.lazy(() => import('./GCP/GCPMain'));
const DevopsMain = React.lazy(() => import('./DevOps/DevopsMain'));
const BlogTen = React.lazy(() => import('./Blogs/BlogTen'));
const BlogEleven = React.lazy(() => import('./Blogs/BlogEleven'));
const BlogTwelve = React.lazy(() => import('./Blogs/BlogTwelve'));
const BlogThirteen = React.lazy(() => import('./Blogs/BlogThirteen'));
const BlogFourteen = React.lazy(() => import('./Blogs/BlogFourteen'));
const BlogFifteen = React.lazy(() => import('./Blogs/BlogFifteen'));
const ReviewMainPage = React.lazy(() => import('./Reviews/ReviewMainPage'));
const FullStackMainPage = React.lazy(() => import('./FullStack/FullStackMainPage'));
const DevopsAzure = React.lazy(() => import('./DevopsAzure/DevopsAzure'));
const BlogSixteen = React.lazy(() => import('./Blogs/BlogSixteen'));
const BlogSeventeen = React.lazy(() => import('./Blogs/BlogSeventeen'));
const BlogEighteen = React.lazy(() => import('./Blogs/BlogEighteen'));
const BlogNineteen = React.lazy(() => import('./Blogs/BlogNineteen'));
const BlogTwenty = React.lazy(() => import('./Blogs/BlogTwenty'));
const BlogTwentyOne = React.lazy(() => import('./Blogs/BlogTwentyOne'));
const BlogTwentyTwo = React.lazy(() => import('./Blogs/BlogTwentyTwo'));
const BlogTwentyFour = React.lazy(() => import('./Blogs/BlogTwentyFour'));
const BlogTwentyFive = React.lazy(() => import('./Blogs/BlogTwentyFive'));
const BlogTwentySix = React.lazy(() => import('./Blogs/BlogTwentySix'));
const BlogTwentySeven = React.lazy(() => import('./Blogs/BlogTwentySeven'));
const BlogTwentyEight = React.lazy(() => import('./Blogs/BlogTwentyEight'));
const BlogTwentyNine = React.lazy(() => import('./Blogs/BlogTwentyNine'));
const BlogThirty = React.lazy(() => import('./Blogs/BlogThirty'));
const BlogThirtyOne = React.lazy(() => import('./Blogs/BlogThirtyOne'));
const Dashboard = React.lazy(() => import('./BlogsDashboard/Dashboard'));
const AuthorPage = React.lazy(() => import('./BlogsDashboard/AuthorPage'));
const BlogAdminPage = React.lazy(() => import('./BlogsDashboard/BlogAdminPage'));
// Lazy load the components
const CloudCourseMain = React.lazy(() => import('./components/CloudCourseMain'));
const AllFooter = React.lazy(() => import('./Footer/AllFooter'));
const AllHeader = React.lazy(() => import('./Header/AllHeader'));
const StickyFooter = React.lazy(() => import('./Footer/StickyFooter'));
const BlogsPage = React.lazy(() => import('./Blogs/BlogsPage'));
const TermsAndCondition = React.lazy(() => import('./TermsAndCondition/TermsAndCondition'));
const ThankyouPage = React.lazy(() => import('./ThankyouComponent/ThankyouPage'));
const ExitFormV3 = React.lazy(() => import('./DevopsV3/components/Popup/ExitFormV3'));
const DataScienceMain = React.lazy(() => import('./DataScience/DataScienceMain'));

const App = () => {
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [showPopup, setShowPopup] = useState(false);
  const [popupShown, setPopupShown] = useState(false);

  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('config', 'G-N1CE0V9ZXD', {
        page_path: location.pathname,
      });
      window.gtag('config', 'AW-16687929732', {
        page_path: location.pathname,
      });
    }
  }, [location]);

  // Remember the last real page the user was on so /thankyou can send them back
  // there after a form submit. sessionStorage survives the round-trip through the
  // external form handler (it's per-origin + per-tab), which document.referrer does not.
  useEffect(() => {
    if (location.pathname !== '/thankyou') {
      sessionStorage.setItem('returnTo', location.pathname + location.search);
    }
  }, [location]);

  // Site-wide exit-intent popup: triggers once when the cursor leaves the top of the
  // viewport. Mounted here so every route has it; individual pages must NOT add their own.
  useEffect(() => {
    const handleMouseLeave = (event) => {
      if (popupShown) return;
      if (event.clientY <= 0) {
        setShowPopup(true);
        setPopupShown(true);
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [popupShown]);

  return (
    <div className="container">
      {/* GTM / gtag / Clarity / FB pixel are loaded from public/index.html via a
          delayed third-party loader (post-paint) — do not re-inject them here. */}

      {/* Render noscript with dangerouslySetInnerHTML */}
      <div
        dangerouslySetInnerHTML={{
          __html: `
            <!-- Google Tag c (noscript) -->
            <noscript><iframe src="https://www.googletagmanager.com/ns.html?id=GTM-MMQ2GZXH"
            height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>
            <!-- End Google Tag Manager (noscript) -->
            <!-- Facebook Pixel (noscript) -->
            <noscript>
              <img height="1" width="1" style="display:none"
                src="https://www.facebook.com/tr?id=587545047035951&ev=PageView&noscript=1" />
            </noscript>
            <!-- End Facebook Pixel (noscript) -->
          `,
        }}
      />

      <Suspense fallback={<div style={{ margin: "20% auto 50% auto", display: "table" }}><DotLoader color="#8f128e" loading /></div>}>
        {/* <AllHeader />
        <StickyFooter /> */}
        <Routes>
          <Route
            path="/"
            element={
              <MetaWrapper title="PaperLive Learning | Online Training Courses" description="Join our Online Training to gain hands-on experience with real-time projects.">
                <LandingPage />
              </MetaWrapper>
            }
          />
          <Route path="/devops-training-online" element={
            <MetaWrapper title="DevOps Course Online | 100% Placement Guarantee" description="Master DevOps with hands-on training in CI/CD, Docker, Kubernetes, AWS & automation tools. Enroll in the online DevOps course in collaboration with Microsoft and AWS and accelerate your tech career.">
              <DevopsV2Main course={EXECUTIVE_PG_DEVOPS_PROGRAM}/>
            </MetaWrapper>
          } />
          <Route path="/devops-training-online-tih-iit" element={
            <MetaWrapper title="DevOps Course Online " description="Master DevOps with hands-on training in CI/CD, Docker, Kubernetes, AWS & automation tools. Enroll in the online DevOps course in collaboration with Microsoft and AWS and accelerate your tech career.">
              <DevopsV3Main course={EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT}/>
            </MetaWrapper>
          } />
          <Route path="/devops-training-online-tih-iit-bangalore" element={
            <MetaWrapper title="DevOps Course Online in Bangalore " description="Master DevOps in Bangalore with hands-on training in CI/CD, Docker, Kubernetes, AWS & automation tools. Enroll in the online DevOps course in collaboration with Microsoft and AWS.">
              <DevopsV3Main city="Bangalore" course={EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT}/>
            </MetaWrapper>
          } />
          <Route path="/devops-training-online-tih-iit-hyderabad" element={
            <MetaWrapper title="DevOps Course Online in Hyderabad " description="Master DevOps in Hyderabad with hands-on training in CI/CD, Docker, Kubernetes, AWS & automation tools. Enroll in the online DevOps course in collaboration with Microsoft and AWS.">
              <DevopsV3Main city="Hyderabad" course={EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT}/>
            </MetaWrapper>
          } />
          <Route path="/devops-training-online-tih-iit-pune" element={
            <MetaWrapper title="DevOps Course Online in Pune " description="Master DevOps in Pune with hands-on training in CI/CD, Docker, Kubernetes, AWS & automation tools. Enroll in the online DevOps course in collaboration with Microsoft and AWS.">
              <DevopsV3Main city="Pune" course={EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT}/>
            </MetaWrapper>
          } />
          <Route path="/devops-training-online-tih-iit-mumbai" element={
            <MetaWrapper title="DevOps Course Online in Mumbai " description="Master DevOps in Mumbai with hands-on training in CI/CD, Docker, Kubernetes, AWS & automation tools. Enroll in the online DevOps course in collaboration with Microsoft and AWS.">
              <DevopsV3Main city="Mumbai" course={EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT}/>
            </MetaWrapper>
          } />
          <Route path="/devops-training-online-tih-iit-chennai" element={
            <MetaWrapper title="DevOps Course Online in Chennai " description="Master DevOps in Chennai with hands-on training in CI/CD, Docker, Kubernetes, AWS & automation tools. Enroll in the online DevOps course in collaboration with Microsoft and AWS.">
              <DevopsV3Main city="Chennai" course={EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT}/>
            </MetaWrapper>
          } />
          <Route path="/devops-training-online-tih-iit-delhi-ncr" element={
            <MetaWrapper title="DevOps Course Online in Delhi-NCR " description="Master DevOps in Delhi-NCR with hands-on training in CI/CD, Docker, Kubernetes, AWS & automation tools. Enroll in the online DevOps course in collaboration with Microsoft and AWS.">
              <DevopsV3Main city="Delhi-NCR" course={EXECUTIVE_PG_DEVOPS_PROGRAM_SHORT}/>
            </MetaWrapper>
          } />
          <Route path="/devops-and-cloud-training-online" element={
            <MetaWrapper title="DevOps and Cloud Job Ready Program | PaperLive Learning" description="Master DevOps with hands-on training! With real-time projects for practical experience. Train with industry experts, work on live projects, and gain the skills top companies demand." noIndex={true}>
              <DevopsV2Main course={DEVOPS_AND_CLOUD_PROGRAM}/>
            </MetaWrapper>
          } />
          <Route path="/devops-training-online-hyderabad" element={
            <MetaWrapper title="DevOps Course Online in Hyderabad | 100% Placement Guarantee" description="Master DevOps in Hyderabad with hands-on training in CI/CD, Docker, Kubernetes, AWS & automation tools. Enroll in the online DevOps course in collaboration with Microsoft and AWS and accelerate your tech career.">
              <DevopsV2Main city={"Hyderabad"} course={EXECUTIVE_PG_DEVOPS_PROGRAM}/>
            </MetaWrapper>
          } />
          <Route path="/devops-training-online-bangalore" element={
            <MetaWrapper title="DevOps Course Online in Bangalore | 100% Placement Guarantee" description="Master DevOp in Bangalore with hands-on training in CI/CD, Docker, Kubernetes, AWS & automation tools. Enroll in the online DevOps course in collaboration with Microsoft and AWS and accelerate your tech career.">
              <DevopsV2Main city={"Bangalore"} course={EXECUTIVE_PG_DEVOPS_PROGRAM}/>
            </MetaWrapper>
          } />
          <Route path="/devops-training-online-pune" element={
            <MetaWrapper title="DevOps Course Online in Pune | 100% Placement Guarantee" description="Master DevOps in Pune with hands-on training in CI/CD, Docker, Kubernetes, AWS & automation tools. Enroll in the online DevOps course in collaboration with Microsoft and AWS and accelerate your tech career.">
              <DevopsV2Main city={"Pune"} course={EXECUTIVE_PG_DEVOPS_PROGRAM}/>
            </MetaWrapper>
          } />
          <Route path="/devops-training-online-chennai" element={
            <MetaWrapper title="DevOps Course Online in Chennai | 100% Placement Guarantee" description="Master DevOps in Chennai with hands-on training in CI/CD, Docker, Kubernetes, AWS & automation tools. Enroll in the online DevOps course in collaboration with Microsoft and AWS and accelerate your tech career.">
              <DevopsV2Main city={"Chennai"} course={EXECUTIVE_PG_DEVOPS_PROGRAM}/>
            </MetaWrapper>
          } />
          <Route path="/devops-training-online-mumbai" element={
            <MetaWrapper title="DevOps Course Online in Mumbai | 100% Placement Guarantee" description="Master DevOps in Mumbai with hands-on training in CI/CD, Docker, Kubernetes, AWS & automation tools. Enroll in the online DevOps course in collaboration with Microsoft and AWS and accelerate your tech career.">
              <DevopsV2Main city={"Mumbai"} course={EXECUTIVE_PG_DEVOPS_PROGRAM}/>
            </MetaWrapper>
          } />
          <Route path="/devops-training-online-delhi" element={
            <MetaWrapper title="DevOps Course Online in Delhi | 100% Placement Guarantee" description="Master DevOps in Delhi with hands-on training in CI/CD, Docker, Kubernetes, AWS & automation tools. Enroll in the online DevOps course in collaboration with Microsoft and AWS and accelerate your tech career.">
              <DevopsV2Main city={"Delhi"} course={EXECUTIVE_PG_DEVOPS_PROGRAM}/>
            </MetaWrapper>
          } />

          <Route path="/devops-certification-training" element={
            <MetaWrapper title="Devops Certification Training | PaperLive Learning" description="Master DevOps with hands-on training! With real-time projects for practical experience. Train with industry experts, work on live projects, and gain the skills top companies demand." noIndex={true}>
              <DevopsV2Main course={DEVOPS_CERTIFICATION}/>
            </MetaWrapper>
          } />

          <Route path="/aws-solution-architect" element={
            <MetaWrapper title="AWS Solution Architect Program | PaperLive Learning" description="Master AWS with hands-on training! With real-time projects for practical experience. Train with industry experts, work on live projects, and gain the skills top companies demand." noIndex={true}>
              <AWSMain />
            </MetaWrapper>
          } />
          <Route path="/microsoft-azure" element={
            <MetaWrapper title="Microsoft Azure Program | PaperLive Learning" description="Master Microsoft Azure with hands-on training! With real-time projects for practical experience. Train with industry experts, work on live projects, and gain the skills top companies demand." noIndex={true}>
              <AzureMain />
            </MetaWrapper>
          } />
          <Route path="/google-cloud-platform" element={
            <MetaWrapper title="Google Cloud Platform Program | PaperLive Learning" description="Master Google Cloud Platform with hands-on training! With real-time projects for practical experience. Train with industry experts, work on live projects, and gain the skills top companies demand." noIndex={true}>
              <GCPMain />
            </MetaWrapper>
          } />
          <Route path="/blog" element={
            <MetaWrapper title="Blog | PaperLive Learning">
              <BlogsPage />
            </MetaWrapper>
          } />
          <Route path="/terms" element={
            <MetaWrapper title="Terms and Conditions | PaperLive Learning">
              <TermsAndCondition />
            </MetaWrapper>
          } />
          <Route path="/refund-policy" element={
            <MetaWrapper title="Refund Policy | PaperLive Learning">
              <RefundPolicy />
            </MetaWrapper>
          } />
          <Route path="/thankyou" element={
            <MetaWrapper title="Thankyou | PaperLive Learning">
              <ThankyouPage />
            </MetaWrapper>
          } />
          <Route path="/about-us" element={
            <MetaWrapper title="About Us | PaperLive Learning" >
              <About />
            </MetaWrapper>
          } />
          <Route path="/privacy-policy" element={
            <MetaWrapper title="Privacy Policy | PaperLive Learning" >
              <Privacy />
            </MetaWrapper>
          } />

          {/* Blogs */}
          <Route path="/blog/what-is-devOps-deployment-and-how-does-it-function-in-devops" element={
            <MetaWrapper title="What is DevOps Deployment and How Does It Function in DevOps?" >
              <BlogOne />
            </MetaWrapper>
          } />
          <Route path="/blog/over-60+-azure-devops-interview-questions-and-answers-to-prepare-for-2025" element={
            <MetaWrapper title="Over 60+ Azure DevOps Interview Questions and Answers to Prepare for 2025" >
              <BlogTwo />
            </MetaWrapper>
          } />
          <Route path="/blog/what-is-cloud-consulting-and-what-advantages-does-it-offer-to-businesses" element={
            <MetaWrapper title="What is cloud consulting and what advantages does it offer to businesses?" >
              <BlogEight />
            </MetaWrapper>
          } />
          <Route path="/blog/from-college-to-devops-a-roadmap-for-freshers" element={
            <MetaWrapper title="From College to DevOps: A Roadmap for Freshers" >
              <BlogTen />
            </MetaWrapper>
          } />
          <Route path="/blog/the-importance-of-soft-skills-in-a-devops-career" element={
            <MetaWrapper title="The Importance of Soft Skills in a DevOps Career" >
              <BlogThirteen />
            </MetaWrapper>
          } />
          <Route path="/blog/certifications-that-add-value-to-your-resume-in-2025-aws-azure-and-devops" element={
            <MetaWrapper title="Certifications That Add Value to Your Resume in 2025: AWS, Azure, and DevOps" >
              <BlogFourteen />
            </MetaWrapper>
          } />
          <Route path="/blog/what-is-aws-devops-certification-a-complete-beginners-guide" element={
            <MetaWrapper title="AWS DevOps Certification: Your Complete 2025 Career Guide" >
              <BlogTwenty />
            </MetaWrapper>
          } />
          <Route path="/blog/the-future-of-devops-certification-what-it-pros-need-to-know" element={
            <MetaWrapper title="The Future of DevOps Certification: What IT Pros Need to Know" >
              <BlogTwentyTwo />
            </MetaWrapper>
          } />
          <Route path="/blog/what-makes-jenkins-and-docker-essential-in-a-devops-certification" element={
            <MetaWrapper title="Jenkins & Docker in DevOps Certification | Paperlive Learning" >
              <BlogTwentySix />
            </MetaWrapper>
          } />
          <Route path="/blog/why-kubernetes-terraform-are-must-have-skills-in-any-devops-certification" element={
            <MetaWrapper title="Kubernetes & Terraform: Skills in DevOps Certification | Paperlive Learning" >
              <BlogTwentyEight />
            </MetaWrapper>
          } />
          <Route path="/blog/how-multi-cloud-and-gen-ai-are-reshaping-online-devops-certification" element={
            <MetaWrapper title="How Multi-Cloud & Gen AI Are Transforming DevOps Certification in 2025 | Paperlive Learning" >
              <BlogTwentyNine />
            </MetaWrapper>
          } />
          <Route path="/blog/azure-devops-vs-aws-devops-which-one-you-should-learn-in-2025" element={
            <MetaWrapper title="Azure DevOps vs AWS DevOps: Best Choice for 2025 | Paperlive Learning" >
              <BlogThirtyOne />
            </MetaWrapper>
          } />

          <Route path="/reviews" element={
            <MetaWrapper title="PaperLive Learning Reviews | PaperLive Learning" description="Read actual user reviews of Paperlive Learning to learn more about the course's quality, advantages, and general educational experiences. See more by clicking!">
              <ReviewMainPage />
            </MetaWrapper>
          } />

          <Route
            path="/data-science-training-online"
            element={
              <MetaWrapper title="Data Science Course with Placement Assistance | 100% Placement Guarantee" description="A live, instructor-led data science online course — 150+ hours across 10 modules, from Python, SQL and statistics to machine learning, deep learning, MLOps and Generative & Agentic AI.">
                <DataScienceMain />
              </MetaWrapper>
            }
          />

          <Route path="/data-science-training-online-hyderabad" element={
            <MetaWrapper title="Data Science Course in Hyderabad | 100% Placement Guarantee" description="A live, instructor-led data science online course in Hyderabad — 150+ hours across 10 modules, from Python, SQL and statistics to machine learning, deep learning, MLOps and Generative & Agentic AI.">
              <DataScienceMain city={"Hyderabad"} />
            </MetaWrapper>
          } />
          <Route path="/data-science-training-online-bangalore" element={
            <MetaWrapper title="Data Science Course in Bangalore | 100% Placement Guarantee" description="A live, instructor-led data science online course in Bangalore — 150+ hours across 10 modules, from Python, SQL and statistics to machine learning, deep learning, MLOps and Generative & Agentic AI.">
              <DataScienceMain city={"Bangalore"} />
            </MetaWrapper>
          } />
          <Route path="/data-science-training-online-pune" element={
            <MetaWrapper title="Data Science Course in Pune | 100% Placement Guarantee" description="A live, instructor-led data science online course in Pune — 150+ hours across 10 modules, from Python, SQL and statistics to machine learning, deep learning, MLOps and Generative & Agentic AI.">
              <DataScienceMain city={"Pune"} />
            </MetaWrapper>
          } />
          <Route path="/data-science-training-online-chennai" element={
            <MetaWrapper title="Data Science Course in Chennai | 100% Placement Guarantee" description="A live, instructor-led data science online course in Chennai — 150+ hours across 10 modules, from Python, SQL and statistics to machine learning, deep learning, MLOps and Generative & Agentic AI.">
              <DataScienceMain city={"Chennai"} />
            </MetaWrapper>
          } />
          <Route path="/data-science-training-online-mumbai" element={
            <MetaWrapper title="Data Science Course in Mumbai | 100% Placement Guarantee" description="A live, instructor-led data science online course in Mumbai — 150+ hours across 10 modules, from Python, SQL and statistics to machine learning, deep learning, MLOps and Generative & Agentic AI.">
              <DataScienceMain city={"Mumbai"} />
            </MetaWrapper>
          } />
          <Route path="/data-science-training-online-delhi" element={
            <MetaWrapper title="Data Science Course in Delhi | 100% Placement Guarantee" description="A live, instructor-led data science online course in Delhi — 150+ hours across 10 modules, from Python, SQL and statistics to machine learning, deep learning, MLOps and Generative & Agentic AI.">
              <DataScienceMain city={"Delhi"} />
            </MetaWrapper>
          } />

          <Route
            path="/full-stack-web-development"
            element={
              <MetaWrapper title="Full Stack Web Development | PaperLive Learning" description="Join our Online Training to gain hands-on experience with real-time projects." noIndex={true}>
                <FullStackMainPage />
              </MetaWrapper>
            }
          />

        <Route
            path="/blog/dashboard"
            element={
              <MetaWrapper title="Blog Dashboard | PaperLive Learning" description="">
                <BlogAdminPage />
              </MetaWrapper>
            }
          />

          <Route
            path="/blog/master-devops-fundamentals-with-an-online-devops-course-a-beginners-roadmap-to-success-2026"
            element={<Navigate to="/blog/devops-fundamentals" replace />}
          />

          <Route path="/blog/:slug" element={<BlogDetail />} />

          <Route path="/blog/author/:authorName" element={<AuthorPage />} />


          {/* Never sitemap this - Page Not Found */}
          <Route
            path="*"
            element={
              <MetaWrapper title="Page Not Found | PaperLive Learning">
                <ErrorPage />
              </MetaWrapper>
            }
          />

        </Routes>
        {/* <Chatbot /> */}
        {/* <AllFooter /> */}
      </Suspense>

      {/* Only mount the exit-intent popup once it's actually been triggered. */}
      {popupShown && (
        <Suspense fallback={null}>
          <ExitFormV3
            isSmallScreen={isSmallScreen}
            open={showPopup}
            onClose={() => setShowPopup(false)}
          />
        </Suspense>
      )}
    </div>
  );
}

const AppWithRouter = () => (
  <BrowserRouter>
    <App />
  </BrowserRouter>
);

export default AppWithRouter;
